import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, ArrowLeft, Users, UserPlus, Plus, RefreshCw,
  Shield, ArrowRight, MapPin, Phone
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';

export default function CommunitiesListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const functions = getFunctions();

  // ✅ ALL hooks must be called before any conditional return
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadCommunities = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const listFn = httpsCallable(functions, 'listCommunities');
      const result = await listFn();

      if (result.data.success) {
        setCommunities(result.data.communities);
      }
    } catch (err) {
      console.error('Error loading communities:', err);
      setError('Failed to load communities. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [functions]);

  useEffect(() => {
    // Only fetch if the user is actually a super admin
    if (user?.isSuperAdmin) {
      loadCommunities();
    } else {
      setLoading(false);
    }
  }, [user?.isSuperAdmin, loadCommunities]);

  // ✅ Derived values (no hooks) — safe after all hooks
  const totalResidents = communities.reduce((sum, c) => sum + (c.residentCount || 0), 0);
  const totalStaff = communities.reduce((sum, c) => sum + (c.staffCount || 0), 0);

  // ✅ Conditional return AFTER all hooks
  if (!user?.isSuperAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md text-center">
          <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-500 mb-4">Only platform administrators can view all communities.</p>
          <button onClick={() => navigate('/')} className="text-quiltt-purple hover:underline">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">All Communities</h1>
                <p className="text-sm text-gray-500">
                  {communities.length} communit{communities.length !== 1 ? 'ies' : 'y'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={loadCommunities}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/community/setup')}
                className="flex items-center gap-2 bg-quiltt-purple hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                New Community
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {/* Summary stats */}
        {!loading && communities.length > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <p className="text-2xl font-bold text-gray-900">{communities.length}</p>
              <p className="text-sm text-gray-500">Communities</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <p className="text-2xl font-bold text-blue-600">{totalResidents}</p>
              <p className="text-sm text-gray-500">Total Residents</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <p className="text-2xl font-bold text-purple-600">{totalStaff}</p>
              <p className="text-sm text-gray-500">Total Staff</p>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <div className="w-10 h-10 border-3 border-quiltt-purple border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading communities...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={loadCommunities} className="text-quiltt-purple hover:underline">
              Try Again
            </button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && communities.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No communities yet</h3>
            <p className="text-gray-500 mb-6">Create your first community to get started.</p>
            <button
              onClick={() => navigate('/community/setup')}
              className="inline-flex items-center gap-2 bg-quiltt-purple hover:bg-purple-700 text-white font-medium py-2.5 px-5 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Community
            </button>
          </div>
        )}

        {/* Communities list */}
        {!loading && communities.length > 0 && (
          <div className="space-y-4">
            {communities.map(community => (
              <div
                key={community.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-quiltt-purple/20 transition-all cursor-pointer group"
                onClick={() => navigate(`/community/${community.id}`)}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 bg-quiltt-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-quiltt-purple" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">{community.name}</h3>
                        {community.address && (
                          <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3.5 h-3.5" />
                            {community.address}
                          </p>
                        )}
                        {community.phone && (
                          <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                            <Phone className="w-3.5 h-3.5" />
                            {community.phone}
                          </p>
                        )}
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-quiltt-purple transition-colors mt-1" />
                  </div>

                  <div className="flex items-center gap-6 text-sm">
                    <span className="flex items-center gap-1.5 text-gray-600">
                      <Users className="w-4 h-4 text-gray-400" />
                      {community.residentCount} resident{community.residentCount !== 1 ? 's' : ''}
                    </span>
                    <span className="flex items-center gap-1.5 text-gray-600">
                      <UserPlus className="w-4 h-4 text-gray-400" />
                      {community.staffCount} staff
                    </span>
                    {community.admins?.length > 0 && (
                      <span className="text-gray-400">
                        Admin: {community.admins.map(a => a.name || a.email).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
